// ModelListRow — the list-view row on the /models catalog page.
//
// Two layouts share this component:
//   - Wide (`!isXS`): true tabular row with fixed-width columns
//     [Model · Company · Inputs · Outputs · Cost], rendered inside a
//     horizontally-scrollable container so narrow desktops don't squash
//     the chips.
//   - XS (mobile): the same data restacked vertically as a tappable
//     card — logo + name on top, then a small line of company / cost
//     metadata, then I/O chips inline. Avoids the table-on-phone
//     anti-pattern while keeping the same fields.
//
// Inputs/outputs are spelled out with text labels (Image / Video / etc.)
// rather than icon-only cells, since "what does this take and produce"
// is the question the list view exists to answer at a glance.

import React, { useMemo, useState } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { Image } from 'expo-image';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import ModelCreditsButton from './ModelCreditsButton';
import ModelPriceTier from './ModelPriceTier';
import {
  getInputChips,
  getOutputChips,
  isNewModel,
} from '@/utils/model_catalog';
import { PLUS_COLORS } from '@/constants/Colors';

// Wide-view column config. Kept in sync with the header in the page
// via the shared `listColumnStyle` helper. Columns marked `flex` stretch
// to fill remaining width (Model + I/O); the rest stay fixed (Company,
// Cost) where the content is short or fixed-format.
export const CATALOG_COLS = [
  { id: 'name',    label: 'Model',   flex: 2, minWidth: 220 },
  { id: 'company', label: 'Company', width: 140 },
  { id: 'inputs',  label: 'Inputs',  flex: 1, minWidth: 140 },
  { id: 'outputs', label: 'Outputs', flex: 1, minWidth: 140 },
  { id: 'cost',    label: 'Starts at', width: 210, align: 'right' },
];

// Mirror of `listColumnStyle` from the page, kept here so the row can
// render itself without having to import from the page (would cause a
// circular dep).
const cellStyleFor = (c) => {
  if (c.flex) return { flex: c.flex, minWidth: c.minWidth || 0, paddingRight: 12 };
  return { width: c.width };
};

// Inputs/Outputs cell — spells out each chip rather than just an icon.
const IOChips = ({ chips, color }) => {
  if (!chips || chips.length === 0) {
    return <Text size="xs" color="tertiary">—</Text>;
  }
  return (
    <View style={ioStyles.row}>
      {chips.map(c => (
        <View key={c.key} style={[ioStyles.chip, { backgroundColor: `${color}1F` }]}>
          <Icon name={c.icon} size={9} color={color} />
          <Text size="xxs" weight="semibold" style={{ color }}>{c.label}</Text>
        </View>
      ))}
    </View>
  );
};

const ioStyles = StyleSheet.create({
  row: { flexDirection: 'row', flexWrap: 'wrap', gap: 4 },
  chip: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    paddingHorizontal: 6, height: 20, borderRadius: 4,
  },
});

const Logo = ({ model, size, colors }) => (
  <View
    style={{
      width: size, height: size, borderRadius: Math.round(size / 4.5),
      backgroundColor: model.logoPath ? '#fff' : colors.solid_color_tertiary,
      alignItems: 'center', justifyContent: 'center',
      overflow: 'hidden',
    }}
  >
    {model.logoPath
      ? <Image source={{ uri: model.logoPath }} style={{ width: size, height: size }} contentFit="cover" />
      : <Icon name="sparkles" size={Math.round(size * 0.36)} color={PLUS_COLORS.textColorPurple} />
    }
  </View>
);

const NameMeta = ({ model, colors }) => {
  const isNew = isNewModel(model);
  return (
    <View style={{ flex: 1, minWidth: 0 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
        <Text size="s" weight="semibold" numberOfLines={1} style={{ flexShrink: 1 }}>{model.name}</Text>
        {isNew && <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: '#FF3B30' }} />}
        {model?.flags?.isPlusOnly && (
          <Icon name="sparkles" solid size={9} color={PLUS_COLORS.textColorPurple} />
        )}
      </View>
    </View>
  );
};

const ModelListRow = ({ model, company, onPress, isXS }) => {
  const { colors } = useTheme();
  const [hovered, setHovered] = useState(false);
  const inputs  = getInputChips(model);
  const outputs = getOutputChips(model);

  const styles = useMemo(() => createStyles(colors), [colors]);

  if (!model) return null;

  // ---------- XS / mobile: vertical card ----------
  if (isXS) {
    return (
      <Pressable
        onPress={() => onPress?.(model.id)}
        style={[
          styles.mobileCard,
          { backgroundColor: hovered ? colors.solid_color_tertiary : colors.solid_color_block },
        ]}
        onHoverIn={() => setHovered(true)}
        onHoverOut={() => setHovered(false)}
        android_ripple={{ color: colors.solid_color_tertiary }}
      >
        <View style={styles.mobileHeader}>
          <Logo model={model} size={36} colors={colors} />
          <NameMeta model={model} colors={colors} />
          <ModelPriceTier model={model} />
        </View>
        <ModelCreditsButton model={model} />
        <View style={styles.mobileChips}>
          {company?.name && (
            <View style={[styles.mobileMetaPill, { backgroundColor: colors.solid_color_tertiary }]}>
              <Text size="xxs" color="tertiary" numberOfLines={1}>{company.name}</Text>
            </View>
          )}
          {inputs.length > 0 && (
            <View style={styles.mobileIOGroup}>
              <Text size="xxs" color="tertiary" weight="semibold">In:</Text>
              <IOChips chips={inputs} color="#3B82F6" />
            </View>
          )}
          {outputs.length > 0 && (
            <View style={styles.mobileIOGroup}>
              <Text size="xxs" color="tertiary" weight="semibold">Out:</Text>
              <IOChips chips={outputs} color="#22C55E" />
            </View>
          )}
        </View>
      </Pressable>
    );
  }

  // ---------- Wide: table row ----------
  return (
    <Pressable
      onPress={() => onPress?.(model.id)}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={[
        styles.row,
        {
          backgroundColor: hovered ? colors.solid_color_tertiary : 'transparent',
          borderBottomColor: colors.border_color_secondary,
        },
        Platform.OS === 'web' && { cursor: 'pointer' },
      ]}
    >
      <View style={[styles.cell, cellStyleFor(CATALOG_COLS[0]), { gap: 10 }]}>
        <Logo model={model} size={32} colors={colors} />
        <NameMeta model={model} colors={colors} />
      </View>
      <View style={[styles.cell, cellStyleFor(CATALOG_COLS[1])]}>
        <Text size="xs" color="secondary" numberOfLines={1}>{company?.name || '—'}</Text>
      </View>
      <View style={[styles.cell, cellStyleFor(CATALOG_COLS[2])]}>
        <IOChips chips={inputs} color="#3B82F6" />
      </View>
      <View style={[styles.cell, cellStyleFor(CATALOG_COLS[3])]}>
        <IOChips chips={outputs} color="#22C55E" />
      </View>
      <View style={[styles.cell, cellStyleFor(CATALOG_COLS[4]), { flexDirection: 'column', alignItems: 'flex-end', gap: 3 }]}>
        <ModelPriceTier model={model} />
        <ModelCreditsButton model={model} />
      </View>
    </Pressable>
  );
};

ModelListRow.propTypes = {
  model: PropTypes.object,
  company: PropTypes.object,
  onPress: PropTypes.func,
  isXS: PropTypes.bool,
};

const createStyles = (colors) => StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 56,
    paddingHorizontal: 16, paddingVertical: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  cell: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  mobileCard: {
    padding: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    gap: 10,
  },
  mobileHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  mobileCost: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
  },
  mobileChips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 6,
  },
  mobileMetaPill: {
    paddingHorizontal: 6, height: 18, borderRadius: 4,
    justifyContent: 'center',
  },
  mobileIOGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
});

export default React.memo(ModelListRow);
